/**
 * Pure math for the Trends "Highlights" tab — the monthly fitness composite and
 * the loud-listening day count. Kept DOM-free so the "only average available
 * components" and "no NaN propagation" invariants are unit-tested directly.
 */

import type { MetricPoint } from '../api.js';

/** Population mean + standard deviation of a numeric array (sd = 0 when n < 2). */
export function meanSd(vals: readonly number[]): { mean: number; sd: number } {
  const clean = vals.filter((v) => Number.isFinite(v));
  const n = clean.length;
  if (n === 0) return { mean: 0, sd: 0 };
  const mean = clean.reduce((a, b) => a + b, 0) / n;
  if (n < 2) return { mean, sd: 0 };
  const variance = clean.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  return { mean, sd: Math.sqrt(variance) };
}

/** One fitness-composite input series over ~1y, day-bucketed. */
export interface CompositeComponent {
  key: string;
  points: readonly MetricPoint[];
  /** True when a lower raw value is fitter (resting HR, recovery bpm). */
  invert: boolean;
}

/** One month of the composite: the blended z plus each component's monthly z. */
export interface CompositePoint {
  /** YYYY-MM. */
  month: string;
  /** Mean of the available component z's that month; null when none had data. */
  composite: number | null;
  /** Per-component monthly-mean z (null where that component had no data). */
  components: Record<string, number | null>;
}

function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

/**
 * Standardize each component against its own 1-year mean/sd (inverting where a
 * lower value is fitter), bucket the z-scores by month, then blend: each output
 * month averages only the components that actually had data that month, so a
 * gap in one signal never poisons the composite with NaN. Months are the sorted
 * union across all components; a month with no component data yields a null
 * composite (a break in the line rather than a fabricated zero).
 */
export function fitnessComposite(components: readonly CompositeComponent[]): CompositePoint[] {
  const keys = components.map((c) => c.key);

  // Per component: monthly-mean z, keyed by month.
  const perComponent = new Map<string, Map<string, number>>();
  const months = new Set<string>();

  for (const comp of components) {
    const vals = comp.points.map((p) => p.v).filter((v) => Number.isFinite(v));
    const { mean, sd } = meanSd(vals);
    const byMonth = new Map<string, number[]>();
    if (sd > 0) {
      for (const p of comp.points) {
        if (!Number.isFinite(p.v)) continue;
        const z = ((p.v - mean) / sd) * (comp.invert ? -1 : 1);
        const m = monthOf(p.t);
        months.add(m);
        (byMonth.get(m) ?? byMonth.set(m, []).get(m)!).push(z);
      }
    }
    const monthlyZ = new Map<string, number>();
    for (const [m, zs] of byMonth) monthlyZ.set(m, zs.reduce((a, b) => a + b, 0) / zs.length);
    perComponent.set(comp.key, monthlyZ);
  }

  return [...months]
    .sort()
    .map((month) => {
      const components: Record<string, number | null> = {};
      const available: number[] = [];
      for (const key of keys) {
        const z = perComponent.get(key)?.get(month);
        if (z === undefined) {
          components[key] = null;
        } else {
          components[key] = z;
          available.push(z);
        }
      }
      const composite =
        available.length > 0 ? available.reduce((a, b) => a + b, 0) / available.length : null;
      return { month, composite, components };
    });
}

/** Days whose (day-bucketed) value exceeds `threshold` — e.g. loud-listening days. */
export function countLoudDays(points: readonly MetricPoint[], threshold = 70): number {
  return points.reduce((n, p) => (Number.isFinite(p.v) && p.v > threshold ? n + 1 : n), 0);
}
